import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useRowProps } from 'hooks/useRowProps';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import useFetchMissingItems from 'pages/objectManager/hooks/useFetchMissingItems';
import { calendarClient } from 'services/calendarClient';
import { getErrorMessage } from 'utils/errorUtils';
import { errorNotification } from 'utils/notifications';
import type {
  OutlookConfiguration,
  OutlookConfigurationResponse,
} from '@timeedit/calendar-integration-api-contract';
import { Alert, DefaultContentLayout } from '@timeedit/ui-components';
import { useForm } from '@timeedit/ui-components/antd/form/Form';
import { NEW_ITEM_ID } from '../shared';
import {
  type CreateOutlookConfiguration,
  intlPrefix,
} from './MS365ConfigurationDetails';
import MS365ConfigurationDrawer from './MS365ConfigurationDrawer';
import { MS365ConfigurationList } from './MS365ConfigurationList';

export function MS365ConfigurationPage() {
  const [configuration, setConfiguration] =
    useState<OutlookConfigurationResponse | null>(null);
  const [secondConfiguration, setSecondConfiguration] =
    useState<OutlookConfigurationResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [configurations, setConfigurations] = useState<
    OutlookConfigurationResponse[]
  >([]);

  const mounted = useRef(true);

  const [form] = useForm<CreateOutlookConfiguration>();

  const configurationMap = useMemo(
    () =>
      configurations.reduce<Record<string, OutlookConfigurationResponse>>(
        (acc, config) => {
          acc[config.id] = config;
          return acc;
        },
        {}
      ),
    [configurations]
  );

  useEffect(() => {
    if (configuration) {
      form.setFieldsValue(configuration);
    }
  }, [configuration]);

  const initConfigurations = useCallback(async () => {
    try {
      setLoading(true);

      const res = await calendarClient.outlookConfiguration.getAll();

      setLoading(false);

      if (!mounted.current) {
        return;
      }

      if (res.status !== 200) {
        errorNotification(getErrorMessage(res));
        return;
      }

      setConfigurations(res.body.results);
    } catch (error) {
      setLoading(false);
      errorNotification(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    void initConfigurations();

    return () => {
      mounted.current = false;
    };
  }, [initConfigurations]);

  const selectedIds = useSelectResource(
    'objectId',
    configurationMap,
    configuration,
    secondConfiguration,
    setConfiguration,
    setSecondConfiguration
  );

  useFetchMissingItems<OutlookConfigurationResponse, string>({
    itemIds: selectedIds,
    itemMap: configurationMap,
    fetchItemsFn: initConfigurations,
  });

  const onRowClick = useCallback((config: OutlookConfiguration) => {
    setConfiguration(config);
  }, []);

  const highlightedIds = useMemo(
    () => [configuration?.id, secondConfiguration?.id].filter(Boolean),
    [configuration?.id, secondConfiguration?.id]
  );

  const onRow = useRowProps(onRowClick, highlightedIds);

  const onClose = useCallback((config: OutlookConfiguration) => {
    setConfiguration((prev) => (prev?.id === config.id ? null : prev));
    setSecondConfiguration((prev) => (prev?.id === config.id ? null : prev));
  }, []);

  const onCreate = useCallback(() => {
    setConfiguration({
      id: NEW_ITEM_ID,
      name: '',
      tenantId: '',
      clientId: '',
    });
  }, []);

  const onDelete = useCallback(
    async (config: OutlookConfiguration) => {
      try {
        const { id } = config;

        const response = await calendarClient.outlookConfiguration.delete({
          params: { id },
        });

        if (response.status < 200 || response.status >= 300) {
          return errorNotification(getErrorMessage(response));
        }

        setConfigurations((prevConfigs) => {
          const deletedIndex = prevConfigs.findIndex((c) => c.id === id);
          if (deletedIndex === -1) {
            return prevConfigs;
          }

          const newConfigs = prevConfigs.slice();
          newConfigs.splice(deletedIndex, 1);
          return newConfigs;
        });
        onClose(config);
      } catch (error) {
        errorNotification(getErrorMessage(error));
      }
    },
    [onClose]
  );

  const onSave = useCallback(
    async (config: OutlookConfiguration) => {
      try {
        const { id } = config;

        const savePromise =
          id === NEW_ITEM_ID
            ? calendarClient.outlookConfiguration.create({
                body: config,
              })
            : calendarClient.outlookConfiguration.update({
                params: { id },
                body: config,
              });

        const response = await savePromise;

        if (response.status < 200 || response.status >= 300) {
          return errorNotification(getErrorMessage(response));
        }

        initConfigurations();
        onClose(config);
      } catch (error) {
        errorNotification(getErrorMessage(error));
      }
    },
    [initConfigurations, onClose]
  );

  const onCompare = useCallback(
    (config: OutlookConfigurationResponse) => {
      if (configuration) {
        setSecondConfiguration(config);
      } else {
        setConfiguration(config);
      }
    },
    [configuration]
  );

  const onDuplicate = useCallback((config: OutlookConfigurationResponse) => {
    setConfiguration({
      ...config,
      id: NEW_ITEM_ID,
      name: `${config.name} (new)`,
    });
  }, []);

  const checkDrawerOpen = useCallback(
    () => Boolean(configuration),
    [configuration]
  );

  const menuProps = useCallback(
    (record: OutlookConfigurationResponse) =>
      getMenuProps({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [checkDrawerOpen, onCompare, onDelete, onDuplicate]
  );

  return (
    <DefaultContentLayout
      title={getText(`${intlPrefix}.title`)}
      subtitle={getText(`${intlPrefix}.subtitle`)}
    >
      <div
        className="inner-content--wrapper allow-scroll"
        style={{ padding: '10px' }}
      >
        <LoadingWrapper isLoading={loading}>
          <Alert
            type="warning"
            title={getText(`${intlPrefix}.alert.title`)}
            description={getText(`${intlPrefix}.alert.description`)}
          />
          <MS365ConfigurationList
            dataSource={configurations}
            getMenuProps={menuProps}
            onCreate={onCreate}
            onRow={onRow}
          />
        </LoadingWrapper>
        <MS365ConfigurationDrawer
          config={configuration}
          secondConfig={secondConfiguration}
          onClose={onClose}
          onDelete={onDelete}
          onDiscard={onClose}
          onSave={onSave}
        />
      </div>
    </DefaultContentLayout>
  );
}
